import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CompanyService } from '@ops/shared-types';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-file-upload-button',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './ops-file-upload-button.component.html',
  styleUrl: './ops-file-upload-button.component.scss',
})
export class OpsFileUploadButtonComponent {
  private _isPreview?: boolean;

  @Input() allowedFormats?: Array<string> = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/svg+xml',
    'image/bmp',
    'image/gif',
    'image/webp',
  ];
  @Input() label?: string;
  @Input() fieldUrlName?: string;
  @Input() fieldUrlValue?: string;
  @Input() uploadApiUrl: string;
  @Input() companyId: string;
  @Input() set isPreview(val: boolean) {
    this._isPreview = !!val;
    if (!val) {
      this.backgroundImageUrl = undefined;
    }
  }
  get isPreview(): boolean {
    return this._isPreview;
  }
  @Output() selectedFileUrl = new EventEmitter<string>();

  backgroundImageUrl?: string | ArrayBuffer;

  get uploadedFileImageStyle() {
    const imageUrl = this.backgroundImageUrl || this.fieldUrlValue;
    const styleObj = imageUrl
      ? { backgroundImage: 'url(' + imageUrl + ')' }
      : {};
    return styleObj;
  }

  get isImageUploaded(): boolean {
    return !!(this.fieldUrlValue || this.backgroundImageUrl);
  }

  get allowedFormatFilesView(): string {
    const allowedViews = [...this.allowedFormats];
    const reformatedViews = allowedViews.map((av: string) =>
      av.replace('image/', '.').replace('+xml', '')
    );
    return reformatedViews.join(', ');
  }

  constructor(
    // private store: Store,
    private companyService: CompanyService,
    // private ls: LocalStorageService,
    // private enumService: EnumService,
    private element: ElementRef,
    private translateService: TranslateService
  ) {
    // this.selectedCompany$ = this.companyService.selectedCompany$.asObservable();
  }

  // uploadFile(type: string): void {
  //   // Simulate file upload or open file dialog here
  //   // console.log(`Upload button clicked for: ${type}`);
  //   // In a real-world scenario, you can integrate Angular's file input or service for file uploads.
  // }

  stopPropagation(event) {
    event.stopPropagation();
  }

  onKeyDown(event: KeyboardEvent) {
    // if (event.key === 'Backspace') {
    //   const currentFocusedElement = document.activeElement;
    //   // Get all focusable elements
    //   const focusableElements = Array.from(
    //     document.querySelectorAll(
    //       'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    //     )
    //   ).filter(
    //     (el) =>
    //       !el.hasAttribute('disabled') && el.getAttribute('tabindex') !== '-1'
    //   );
    //   // Find the index of the currently focused element in the list of focusable elements
    //   const currentIndex = focusableElements.indexOf(currentFocusedElement);
    //   // Get the previous focusable element, if it exists
    //   const previousElement = focusableElements[currentIndex - 1];
    //   if (previousElement) {
    //     (previousElement as any).focus();
    //   }
    //   return;
    // }
  }

  readSVGFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  convertSVGToImage(svgText, format) {
    return new Promise((resolve) => {
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL(format));
      };

      img.src = url;
    });
  }

  base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  }

  base64ToFile(base64, fileName, mimeType) {
    const blob = this.base64ToBlob(base64, mimeType);
    return new File([blob], fileName, { type: mimeType });
  }

  async uploadFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (!this.allowedFormats.includes(file.type)) {
        window.alert(
          this.translateService.instant('windowAlert.opsFileUploadButton.invalidFileFormat')
        );
        (event.target as HTMLInputElement).value = '';
        return;
      }

      const imageUrlFinal = [];

      if (file && file.type === 'image/svg+xml') {
        const svgData = await this.readSVGFile(file);
        const imageUrl = await this.convertSVGToImage(svgData, 'image/png'); // Change to 'image/jpeg' for JPEG format
        // document.getElementById('outputImage').src = imageUrl;

        const convertedFile = this.base64ToFile(
          imageUrl,
          new Date().getTime() + '.png',
          'image/png'
        );

        imageUrlFinal.push(convertedFile);
      } else if (file) {
        const newFile = new File(
          [file],
          'new_name_' + new Date().getTime() + '.' + file.name.split('.').pop(),
          {
            type: file.type,
            lastModified: file.lastModified,
          }
        );
        imageUrlFinal.push(newFile);
      }

      if (imageUrlFinal.length) {
        const sub = this.companyService[this.uploadApiUrl](
          this.companyId,
          imageUrlFinal[0]
        ).subscribe(
          (updatedCompany: any) => {
            this.backgroundImageUrl = updatedCompany[this.fieldUrlName];

            this.selectedFileUrl.emit(updatedCompany[this.fieldUrlName]);
            //    console.log('uploadedAvatar', JSON.stringify(uploadedAvatar));
            // this.sessionManager.setCurrentUser(userUpdated);
            // this.store.dispatch(new DisableSettingsEdit());
            (event.target as HTMLInputElement).value = '';
          },
          (error) => {
            //    console.log('Error', error);
            if (error?.error?.message) {
              const errorsList = error?.error?.message.split('.');
              const errorDescription =
                'Update Error\n\nSome fields are invalid...\nPlease check fields:\n\n';

              const fullMessage = this.translateService.instant('windowAlert.opsFileUploadButton.updateError', { errors: errorsList.join('.\n') });
              window.alert(fullMessage);
            }
            (event.target as HTMLInputElement).value = '';
          }
        );
        // this.subs.push(sub);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.backgroundImageUrl = reader.result; // Store the file URL to use it as a background
        };
        reader.readAsDataURL(imageUrlFinal[0]); // Read the file as a data URL
      }
      (event.target as HTMLInputElement).value = '';
    }
  }

  onAvatarChange(_event?: MouseEvent) {
    //    console.log('Avatar change triggered');
    this.element.nativeElement.getElementsByClassName('file-input')[0].click();
  }
}
